/**
 * LotowheelModalTemplates — HTML inline de los modales de LotoWheel.
 *
 * Solo 2 templates (cada uno gigante por la cantidad de inputs):
 *   - config():    modal principal de configuración del sorteo (~640 líneas
 *                  HTML con 7 tabs internos: General, Apariencia, Rueda,
 *                  Algoritmo, Spin, Fichas, Pozos, Acceso, Bloqueos).
 *   - simulator(): modal del simulador (controles + canvas Chart.js).
 *
 * Extraído de modals.js para no engrosar ese catálogo. ModalTemplates
 * delega aquí los IDs de modal lotowheel-config y lotowheel-simulator.
 *
 * Defaults vienen del SSOT shared/config/games/lotowheel/defaults.js —
 * las constantes DEFAULT_* se inyectan inline para placeholder/value
 * iniciales de los inputs.
 */

import {
    DEFAULT_TIMING, DEFAULT_SPIN, DEFAULT_FICHAS, DEFAULT_LIMITES,
    DEFAULT_IDLE, DEFAULT_MONEDA,
} from '@shared/config/games/lotowheel/defaults.js';
import { MonedaService } from '../services/moneda-service.js';
import { TIPOS_DE_RUEDA_PARA_UI_ADMIN } from '../modules/games/lotowheel/lotowheel.js';
import { t } from '../services/i18n-admin.js';
import { icon } from '../utils/icon.js';

export const LotowheelModalTemplates = {
    // ═══════════════════════════════════════════════════════════════════════
    // CONFIG — modal principal del sorteo (7 tabs: General, Apariencia,
    // Rueda, Algoritmo, Spin, Fichas, Pozos, Acceso, Bloqueos).
    // ═══════════════════════════════════════════════════════════════════════
    config() {
        return `
    <div class="modal" id="modalLotowheelConfig" style="display:none;">
      <div class="modal-header">
        <h2 id="modalLotowheelConfigTitle">${t('Configurar Sorteo')}</h2>
        <button class="btn-icon" data-action="app:closeModal">
          ${icon('x')}
        </button>
      </div>
      <div class="modal-body">
        <div class="modal-tabs">
          ${this._tabs()}
        </div>
        <div class="modal-content">
          <input type="hidden" id="lotowheelConfigId">
          ${this._panelGeneral()}
          ${this._panelTipo()}
          ${this._panelResolver()}
          ${this._panelVisual()}
          ${this._panelAcceso()}
          ${this._panelPozosComunes()}
          ${this._panelApuestas()}
          ${this._panelFichas()}
          ${this._panelMoneda()}
          ${this._panelTiming()}
          ${this._panelSpin()}
        </div>
      </div>
      <div class="modal-footer">
        <button class="btn btn-secondary" data-action="lotowheel:cancelConfigModal">${t('Cancelar')}</button>
        <button id="btnGuardarLotowheel" class="btn btn-primary" data-action="lotowheel:saveLotowheelConfig">
          ${icon('check')}
          ${t('Guardar')}
        </button>
      </div>
    </div>`;
    },

    _tabs() {
        const identidad = [
            { id: 'general', icon: 'settings', title: t('General'), desc: t('Nombre e idioma') },
            { id: 'tipo', icon: 'disc', title: t('Rueda'), desc: t('37, 38 o 13 sectores') },
            { id: 'resolver', icon: 'activity', title: t('Algoritmo'), desc: t('Resolver y presets') },
            { id: 'visual', icon: 'settings', title: t('Apariencia'), desc: t('Tema y sonidos') },
            { id: 'acceso', icon: 'users', title: t('Acceso'), desc: t('Visibilidad por operador') },
        ];
        const juego = [
            { id: 'pozos-comunes', icon: 'wallet', title: t('Pozo'), desc: '' },
            { id: 'apuestas', icon: 'percent', title: t('Apuestas'), desc: t('Tipos y multiplicadores') },
            { id: 'fichas', icon: 'disc', title: t('Fichas'), desc: t('Valores y pago max') },
        ];
        const avanzado = [
            { id: 'moneda', icon: 'wallet', title: t('Moneda'), desc: t('Divisa y formato') },
            { id: 'timing', icon: 'activity', title: t('Tiempos'), desc: t('Cierre y avisos') },
            { id: 'spin', icon: 'disc', title: t('Animacion'), desc: t('Giro de la rueda') },
        ];

        const renderTab = (tab, isFirst) => `
          <div class="modal-tab${isFirst ? ' active' : ''}" data-tab="${tab.id}">
            ${icon(tab.icon)}
            <div class="tab-text">
              <span class="tab-title">${tab.title}</span>
              <span class="tab-desc">${tab.desc}</span>
            </div>
          </div>`;

        return `
          <div class="modal-tab-separator">${t('Identidad')}</div>
          ${identidad.map((tab, i) => renderTab(tab, i === 0)).join('')}
          <div class="modal-tab-separator">${t('Juego')}</div>
          ${juego.map(tab => renderTab(tab, false)).join('')}
          <div class="modal-tab-separator collapsible collapsed" id="tabSepAvanzado">
            ${t('Avanzado')} <span class="chevron">▼</span>
          </div>
          <div class="modal-tab-group-collapsible collapsed" id="tabGroupAvanzado">
            ${avanzado.map(tab => renderTab(tab, false)).join('')}
          </div>`;
    },

    _panelGeneral() {
        return `
          <div class="tab-panel active" id="panel-general">
            <div class="form-row">
              <div class="form-group">
                <label class="form-label">${t('Nombre del Sorteo')}</label>
                <input type="text" class="form-input" id="lotowheelConfigNombre" placeholder="${t('Ej: Fortuna Royal')}">
              </div>
              <div class="form-group">
                <label class="form-label">${t('Idioma')}</label>
                <select class="form-select" id="lotowheelConfigIdioma">
                  <option value="es">${t('Espanol')}</option>
                  <option value="en">English</option>
                  <option value="pt">${t('Portugues')}</option>
                </select>
              </div>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label class="form-label">${t('Intervalo (segundos)')}</label>
                <input type="number" class="form-input" id="lotowheelConfigIntervalo" value="60" min="30" max="300">
                <div class="form-hint">${t('Tiempo entre sorteos (30-300s)')}</div>
              </div>
              <div class="form-group">
                <label class="form-label">${t('Estado')}</label>
                <select class="form-select" id="lotowheelConfigActivo">
                  <option value="1">${t('Activo')}</option>
                  <option value="0">${t('Pausado')}</option>
                </select>
              </div>
            </div>
            <div class="danger-zone" id="dangerZone" style="display:none;">
              <hr style="margin: 20px 0; border-color: var(--border-glass);">
              <div class="form-group">
                <label class="form-label" style="color: var(--accent-red);">${t('Zona de Peligro')}</label>
                <button class="btn btn-danger" data-action="lotowheel:deleteLotowheelConfig" style="margin-top:4px;">
                  ${icon('trash-2')}
                  ${t('Eliminar Sorteo')}
                </button>
                <div class="form-hint">${t('Esta accion no se puede deshacer')}</div>
              </div>
            </div>
          </div>`;
    },

    _panelTipo() {
        return `
          <div class="tab-panel" id="panel-tipo">
            <div class="form-group">
              <label class="form-label">${t('Rueda Loterica de la Fortuna')}</label>
              <select class="form-select" id="lotowheelConfigTipoRuleta" data-action="lotowheel:onTipoRuletaChange" style="font-size:1.1rem; padding:12px;">
                ${Object.entries(TIPOS_DE_RUEDA_PARA_UI_ADMIN)
                    .map(
                        ([tipoId, info]) =>
                            `<option value="${tipoId}">${t(info.label)} (${info.sectors} sectores: ${info.range})</option>`
                    )
                    .join('')}
              </select>
            </div>
            <div id="tipoRuletaInfo" style="padding:16px; background:var(--bg-tertiary); border-radius:8px; margin-top:12px; line-height:1.5;"></div>
            <div class="form-hint" style="margin-top:16px; color:var(--accent-orange); font-size:0.95rem;">
              ${icon('ban', 'icon-sm', { style: 'vertical-align:middle; margin-right:6px;' })}
              ${t('Cambiar el tipo de rueda afecta multiplicadores, rangos de apuestas y opciones disponibles')}
            </div>
          </div>`;
    },

    _panelTiming() {
        return `
          <div class="tab-panel" id="panel-timing">
            <div class="form-row">
              <div class="form-group">
                <label class="form-label">${t('Cierre Apuestas (seg)')}</label>
                <input type="number" class="form-input" id="lotowheelConfigCloseBefore" value="${DEFAULT_TIMING.close_before}" min="1" max="30">
                <div class="form-hint">${t('Segundos antes del sorteo para cerrar')}</div>
              </div>
              <div class="form-group">
                <label class="form-label">Warning (seg)</label>
                <input type="number" class="form-input" id="lotowheelConfigWarningTime" value="${DEFAULT_TIMING.warning_time}" min="1" max="60">
                <div class="form-hint">${t('Segundos antes del cierre para avisar')}</div>
              </div>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label class="form-label">${t('Tiempo Min Util (seg)')}</label>
                <input type="number" class="form-input" id="lotowheelConfigMinUsable" value="${DEFAULT_TIMING.min_usable_time}" min="10" max="120">
                <div class="form-hint">${t('Tiempo minimo para permitir apuestas')}</div>
              </div>
              <div class="form-group">
                <label class="form-label">${t('Duracion Spin (seg)')}</label>
                <input type="number" class="form-input" id="lotowheelConfigSpinTime" value="${DEFAULT_TIMING.spin_time}" min="5" max="30">
                <div class="form-hint">${t('Duracion de la animacion de rueda')}</div>
              </div>
            </div>
            <div class="form-hint" style="margin-top:16px; padding:12px; background:var(--bg-tertiary); border-radius:8px;">
              <strong>${t('Timeline ejemplo (intervalo 60s):')}</strong><br>
              0s: ${t('Inicio sorteo')}<br>
              45s: Warning (60 - 10 - 5)<br>
              55s: ${t('Cierre apuestas')} (60 - 5)<br>
              60s: ${t('Resultado y animacion')}
            </div>
          </div>`;
    },

    _panelVisual() {
        return `
          <div class="tab-panel" id="panel-visual">
            <div class="form-group">
              <label class="form-label" id="label-tema-visual">${t('Tema Visual')}</label>
              <div class="theme-grid" id="themeGrid" role="radiogroup" aria-labelledby="label-tema-visual"></div>
            </div>
            <div class="form-group">
              <label class="form-label">${t('Pack de Sonidos')}</label>
              <select class="form-select" id="lotowheelConfigSoundPack">
                <option value="DEFAULT">${t('Clasico')}</option>
                <option value="RETRO_8BIT">Retro 8-Bit</option>
                <option value="HALLOWEEN">Halloween</option>
              </select>
            </div>
            <h4 style="margin:16px 0 8px; font-size:13px; color:var(--text-secondary)">${t('Efectos del Sorteo')}</h4>
            <div class="form-row">
              <div class="form-group">
                <label class="form-label">${t('Efecto Inicio')}</label>
                <select class="form-select" id="lotowheelConfigEffectStart">
                  <option value="orange">Orange Glow</option>
                  <option value="blue">Blue Pulse</option>
                  <option value="none">${t('Ninguno')}</option>
                </select>
              </div>
              <div class="form-group">
                <label class="form-label">${t('Efecto Final')}</label>
                <select class="form-select" id="lotowheelConfigEffectEnd">
                  <option value="burst">Burst</option>
                  <option value="confetti">Confetti</option>
                  <option value="none">${t('Ninguno')}</option>
                </select>
              </div>
            </div>
            <details id="lotowheelConfigZeroAccordion" style="margin:16px 0; background:var(--bg-tertiary); border-radius:8px; border:1px solid var(--border-glass, rgba(255,255,255,0.08)); overflow:hidden;">
              <summary style="padding:12px 14px; cursor:pointer; list-style:none; display:flex; align-items:center; justify-content:space-between; user-select:none;">
                <span style="display:flex; align-items:center; gap:10px;">
                  <span style="font-size:13px; font-weight:600; color:var(--text-secondary);">${t('Visualizacion de los ceros')}</span>
                  <span id="lotowheelConfigZeroAccordionBadge" style="font-size:11px; padding:2px 8px; border-radius:10px; background:rgba(59,130,246,0.15); color:var(--accent-blue); font-family:monospace;">—</span>
                </span>
                <span style="font-size:11px; color:var(--text-muted); transition:transform 0.2s;">▸</span>
              </summary>
              <div style="padding:4px 16px 16px 28px; border-top:1px solid var(--border-glass, rgba(255,255,255,0.05));">
                <div style="margin:10px 0 12px; font-size:11px; color:var(--text-muted);">${t('Como se muestra el cero al jugador en tiquetes, historial y resultados')}</div>
                <div class="form-group">
                  <label class="form-label">${t('Modo')}</label>
                  <div style="display:flex; flex-direction:column; gap:6px;">
                    <label style="display:flex; align-items:center; gap:8px; cursor:pointer; font-size:13px;">
                      <input type="radio" name="lotowheelConfigZeroDisplayMode" value="POR_NUMERO" data-action="lotowheel:onZeroDisplayModeChange" checked>
                      ${t('Por numero del sector (13, 19, 37, 38, 39)')}
                    </label>
                    <label style="display:flex; align-items:center; gap:8px; cursor:pointer; font-size:13px;">
                      <input type="radio" name="lotowheelConfigZeroDisplayMode" value="POR_CEROS" data-action="lotowheel:onZeroDisplayModeChange">
                      ${t('Por ceros (0, 00, 000)')}
                    </label>
                    <label style="display:flex; align-items:center; gap:8px; cursor:pointer; font-size:13px;">
                      <input type="radio" name="lotowheelConfigZeroDisplayMode" value="POR_TEXTO" data-action="lotowheel:onZeroDisplayModeChange">
                      ${t('Texto personalizado (LOTO, LOTO+, LOTO++)')}
                    </label>
                  </div>
                </div>
                <div id="lotowheelConfigZeroTextosGroup" style="display:none;">
                  <label class="form-label">${t('Etiquetas personalizadas por cero')}</label>
                  <div class="form-hint" style="margin:0 0 8px;">${t('Cada cero tiene 2 lineas: arriba (label chico) y abajo (numero grande). El ancho maximo se ajusta al tipo de rueda para que todo se vea limpio.')}</div>
                  <div style="display:flex; gap:10px; align-items:flex-start;" id="lotowheelConfigZeroInputsRow">
                    <!-- Cero 1 -->
                    <div class="form-group" id="lotowheelConfigZeroSlot1" style="flex:1; margin:0;">
                      <div style="font-size:10px; color:var(--text-muted); margin-bottom:4px; text-align:center; text-transform:uppercase; letter-spacing:0.5px;">${t('Cero 1')}</div>
                      <div style="display:flex; flex-direction:column; gap:4px; padding:8px; background:rgba(34,191,8,0.08); border:1px solid rgba(34,191,8,0.25); border-radius:6px;">
                        <input type="text" class="form-input" id="lotowheelConfigZeroTop1" placeholder="LOTO" maxlength="12" data-action="lotowheel:onZeroDisplayLabelInput" style="text-align:center; font-size:12px; padding:6px;">
                        <input type="text" class="form-input" id="lotowheelConfigZeroBot1" placeholder="37" maxlength="12" data-action="lotowheel:onZeroDisplayLabelInput" style="text-align:center; font-size:16px; font-weight:700; padding:6px;">
                        <div id="lotowheelConfigZeroCounter1" style="font-size:9px; color:var(--text-tertiary); text-align:center; font-family:monospace;">—</div>
                      </div>
                    </div>
                    <!-- Cero 2 -->
                    <div class="form-group" id="lotowheelConfigZeroSlot2" style="flex:1; margin:0; display:none;">
                      <div style="font-size:10px; color:var(--text-muted); margin-bottom:4px; text-align:center; text-transform:uppercase; letter-spacing:0.5px;">${t('Cero 2')}</div>
                      <div style="display:flex; flex-direction:column; gap:4px; padding:8px; background:rgba(34,191,8,0.08); border:1px solid rgba(34,191,8,0.25); border-radius:6px;">
                        <input type="text" class="form-input" id="lotowheelConfigZeroTop2" placeholder="LOTO+" maxlength="12" data-action="lotowheel:onZeroDisplayLabelInput" style="text-align:center; font-size:12px; padding:6px;">
                        <input type="text" class="form-input" id="lotowheelConfigZeroBot2" placeholder="38" maxlength="12" data-action="lotowheel:onZeroDisplayLabelInput" style="text-align:center; font-size:16px; font-weight:700; padding:6px;">
                        <div id="lotowheelConfigZeroCounter2" style="font-size:9px; color:var(--text-tertiary); text-align:center; font-family:monospace;">—</div>
                      </div>
                    </div>
                    <!-- Cero 3 -->
                    <div class="form-group" id="lotowheelConfigZeroSlot3" style="flex:1; margin:0; display:none;">
                      <div style="font-size:10px; color:var(--text-muted); margin-bottom:4px; text-align:center; text-transform:uppercase; letter-spacing:0.5px;">${t('Cero 3')}</div>
                      <div style="display:flex; flex-direction:column; gap:4px; padding:8px; background:rgba(34,191,8,0.08); border:1px solid rgba(34,191,8,0.25); border-radius:6px;">
                        <input type="text" class="form-input" id="lotowheelConfigZeroTop3" placeholder="LOTO++" maxlength="12" data-action="lotowheel:onZeroDisplayLabelInput" style="text-align:center; font-size:12px; padding:6px;">
                        <input type="text" class="form-input" id="lotowheelConfigZeroBot3" placeholder="39" maxlength="12" data-action="lotowheel:onZeroDisplayLabelInput" style="text-align:center; font-size:16px; font-weight:700; padding:6px;">
                        <div id="lotowheelConfigZeroCounter3" style="font-size:9px; color:var(--text-tertiary); text-align:center; font-family:monospace;">—</div>
                      </div>
                    </div>
                  </div>
                </div>
                <!-- Preview canvas fiel: renderea sectores zero reales con los colores y tipografia del wheel -->
                <div style="margin-top:12px;">
                  <div style="font-size:10px; color:var(--text-tertiary); margin-bottom:6px; text-transform:uppercase; letter-spacing:0.5px;">${t('Vista previa en la rueda')}</div>
                  <div style="display:flex; gap:10px; align-items:center; justify-content:flex-start; flex-wrap:wrap;">
                    <canvas id="lotowheelConfigZeroCanvas1" width="200" height="70" style="background:#0f1720; border-radius:6px; display:none;"></canvas>
                    <canvas id="lotowheelConfigZeroCanvas2" width="200" height="70" style="background:#0f1720; border-radius:6px; display:none;"></canvas>
                    <canvas id="lotowheelConfigZeroCanvas3" width="200" height="70" style="background:#0f1720; border-radius:6px; display:none;"></canvas>
                    <div id="lotowheelConfigZeroPreviewTextFallback" style="font-size:14px; font-weight:600; color:var(--accent-blue); font-family:monospace;">—</div>
                  </div>
                </div>
              </div>
            </details>

            <h4 style="margin:16px 0 8px; font-size:13px; color:var(--text-secondary)">${t('Modo Espera')}</h4>
            <div class="form-row">
              <div class="form-group">
                <label class="form-label">${t('Mostrar')}</label>
                <select class="form-select" id="lotowheelConfigIdleMode">
                  <option value="all">${t('Todo')}</option>
                  <option value="wheel">${t('Solo Rueda')}</option>
                  <option value="stats">${t('Solo Stats')}</option>
                </select>
              </div>
              <div class="form-group">
                <label class="form-label">${t('Timeout (seg)')}</label>
                <input type="number" class="form-input" id="lotowheelConfigIdleTimeout" value="${DEFAULT_IDLE.timeout}">
              </div>
            </div>

            <h4 style="margin:16px 0 8px; font-size:13px; color:var(--text-secondary)">${t('Logos / Branding')}</h4>
            <div style="margin-bottom:6px; font-size:10px; color:var(--text-muted);">${t('PNG, SVG o WebP con transparencia — max 2MB')}</div>
            <div class="form-row" style="gap:12px;">
              <div class="form-group" style="flex:1; text-align:center;">
                <label class="form-label" style="font-size:11px;">${t('Logo Centro Rueda')}</label>
                <div id="brandingSlotLogoLoteria" class="branding-slot" data-slot="logoLoteria" style="height:72px; display:flex; align-items:center; justify-content:center;">
                  <img id="brandingPreviewLogoLoteria" src="" alt="" style="max-width:90px; height:64px; display:none; border-radius:6px; background:#f0f0f3; padding:4px; object-fit:contain;">
                  <span id="brandingLabelLogoLoteria" style="font-size:11px; color:var(--text-muted); display:none;"></span>
                </div>
                <div style="font-size:9px; color:var(--text-muted); margin-top:2px;">${t('Se ajustará a 3:1')}</div>
                <div style="margin-top:4px; display:flex; gap:4px; justify-content:center;">
                  <button type="button" class="btn btn-sm" data-action="lotowheel:uploadBrandingAsset" data-param="logoLoteria" style="font-size:11px; padding:2px 8px;">${t('Cambiar')}</button>
                  <button type="button" class="btn btn-sm btn-danger" data-action="lotowheel:resetBrandingAsset" data-param="logoLoteria" style="font-size:11px; padding:2px 8px;">Reset</button>
                </div>
                <input type="file" id="brandingFileLogoLoteria" accept=".png,.svg,.webp" style="display:none;" data-action="lotowheel:onBrandingFileSelected" data-param="logoLoteria">
              </div>
              <div class="form-group" style="flex:1; text-align:center;">
                <label class="form-label" style="font-size:11px;">${t('Logo Zero')}</label>
                <div id="brandingSlotLogoBeneficencia" class="branding-slot" data-slot="logoBeneficencia" style="height:72px; display:flex; align-items:center; justify-content:center;">
                  <img id="brandingPreviewLogoBeneficencia" src="" alt="" style="max-width:90px; height:64px; display:none; border-radius:6px; background:#f0f0f3; padding:4px; object-fit:contain;">
                  <span id="brandingLabelLogoBeneficencia" style="font-size:11px; color:var(--text-muted); display:none;"></span>
                </div>
                <div style="font-size:9px; color:var(--text-muted); margin-top:2px;">${t('Se ajustará a 1:1')}</div>
                <div style="margin-top:4px; display:flex; gap:4px; justify-content:center;">
                  <button type="button" class="btn btn-sm" data-action="lotowheel:uploadBrandingAsset" data-param="logoBeneficencia" style="font-size:11px; padding:2px 8px;">${t('Cambiar')}</button>
                  <button type="button" class="btn btn-sm btn-danger" data-action="lotowheel:resetBrandingAsset" data-param="logoBeneficencia" style="font-size:11px; padding:2px 8px;">Reset</button>
                </div>
                <input type="file" id="brandingFileLogoBeneficencia" accept=".png,.svg,.webp" style="display:none;" data-action="lotowheel:onBrandingFileSelected" data-param="logoBeneficencia">
              </div>
              <div class="form-group" style="flex:1; text-align:center;">
                <label class="form-label" style="font-size:11px;">${t('Logo Sorteo')}</label>
                <div id="brandingSlotLogoSorteo" class="branding-slot" data-slot="logoSorteo" style="height:72px; display:flex; align-items:center; justify-content:center;">
                  <img id="brandingPreviewLogoSorteo" src="" alt="" style="max-width:90px; height:64px; display:none; border-radius:6px; background:#f0f0f3; padding:4px; object-fit:contain;">
                  <span id="brandingLabelLogoSorteo" style="font-size:11px; color:var(--text-muted); display:none;"></span>
                </div>
                <div style="font-size:9px; color:var(--text-muted); margin-top:2px;">${t('Se ajustará a 2:1')}</div>
                <div style="margin-top:4px; display:flex; gap:4px; justify-content:center;">
                  <button type="button" class="btn btn-sm" data-action="lotowheel:uploadBrandingAsset" data-param="logoSorteo" style="font-size:11px; padding:2px 8px;">${t('Cambiar')}</button>
                  <button type="button" class="btn btn-sm btn-danger" data-action="lotowheel:resetBrandingAsset" data-param="logoSorteo" style="font-size:11px; padding:2px 8px;">Reset</button>
                </div>
                <input type="file" id="brandingFileLogoSorteo" accept=".png,.svg,.webp" style="display:none;" data-action="lotowheel:onBrandingFileSelected" data-param="logoSorteo">
              </div>
            </div>

            <h4 style="margin:16px 0 8px; font-size:13px; color:var(--text-secondary)">${t('Assets para Integradores')}</h4>
            <div style="margin-bottom:6px; font-size:10px; color:var(--text-muted);">${t('Imágenes que aparecen en el catálogo de juegos del operador externo')}</div>
            <div class="form-row" style="gap:12px;">
              <div class="form-group" style="flex:1; text-align:center;">
                <label class="form-label" style="font-size:11px;">${t('Icono (cuadrado)')}</label>
                <div id="brandingSlotIconUrl" class="branding-slot" data-slot="iconUrl" style="height:72px; display:flex; align-items:center; justify-content:center;">
                  <img id="brandingPreviewIconUrl" src="" alt="" style="max-width:64px; height:64px; display:none; border-radius:6px; background:#f0f0f3; padding:4px; object-fit:contain;">
                  <span id="brandingLabelIconUrl" style="font-size:11px; color:var(--text-muted); display:none;"></span>
                </div>
                <div style="font-size:9px; color:var(--text-muted); margin-top:2px;">${t('1:1 — Lobby del casino')}</div>
                <div style="margin-top:4px; display:flex; gap:4px; justify-content:center;">
                  <button type="button" class="btn btn-sm" data-action="lotowheel:uploadBrandingAsset" data-param="iconUrl" style="font-size:11px; padding:2px 8px;">${t('Cambiar')}</button>
                  <button type="button" class="btn btn-sm btn-danger" data-action="lotowheel:resetBrandingAsset" data-param="iconUrl" style="font-size:11px; padding:2px 8px;">Reset</button>
                </div>
                <input type="file" id="brandingFileIconUrl" accept=".png,.svg,.webp" style="display:none;" data-action="lotowheel:onBrandingFileSelected" data-param="iconUrl">
              </div>
              <div class="form-group" style="flex:1; text-align:center;">
                <label class="form-label" style="font-size:11px;">${t('Thumbnail (banner)')}</label>
                <div id="brandingSlotThumbnailUrl" class="branding-slot" data-slot="thumbnailUrl" style="height:72px; display:flex; align-items:center; justify-content:center;">
                  <img id="brandingPreviewThumbnailUrl" src="" alt="" style="max-width:120px; height:64px; display:none; border-radius:6px; background:#f0f0f3; padding:4px; object-fit:contain;">
                  <span id="brandingLabelThumbnailUrl" style="font-size:11px; color:var(--text-muted); display:none;"></span>
                </div>
                <div style="font-size:9px; color:var(--text-muted); margin-top:2px;">${t('16:9 — Card del catálogo')}</div>
                <div style="margin-top:4px; display:flex; gap:4px; justify-content:center;">
                  <button type="button" class="btn btn-sm" data-action="lotowheel:uploadBrandingAsset" data-param="thumbnailUrl" style="font-size:11px; padding:2px 8px;">${t('Cambiar')}</button>
                  <button type="button" class="btn btn-sm btn-danger" data-action="lotowheel:resetBrandingAsset" data-param="thumbnailUrl" style="font-size:11px; padding:2px 8px;">Reset</button>
                </div>
                <input type="file" id="brandingFileThumbnailUrl" accept=".png,.svg,.webp" style="display:none;" data-action="lotowheel:onBrandingFileSelected" data-param="thumbnailUrl">
              </div>
            </div>
          </div>`;
    },

    _panelMoneda() {
        return `
          <div class="tab-panel" id="panel-moneda">
            <div class="form-group">
              <label class="form-label">${t('Moneda')}</label>
              <select class="form-select" id="lotowheelConfigMonedaCodigo" data-action="lotowheel:onMonedaChange">
                ${MonedaService.buildSelectorOptions()}
              </select>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label class="form-label">${t('Simbolo')}</label>
                <input type="text" class="form-input" id="lotowheelConfigMonedaSimbolo" value="${DEFAULT_MONEDA.simbolo}">
              </div>
              <div class="form-group">
                <label class="form-label">${t('Decimales')}</label>
                <input type="number" class="form-input" id="lotowheelConfigMonedaDecimales" value="${DEFAULT_MONEDA.decimales}" min="0" max="4">
              </div>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label class="form-label">${t('Separador Decimal')}</label>
                <input type="text" class="form-input" id="lotowheelConfigMonedaSepDec" value="${DEFAULT_MONEDA.separadorDecimal}" maxlength="1">
              </div>
              <div class="form-group">
                <label class="form-label">${t('Separador Miles')}</label>
                <input type="text" class="form-input" id="lotowheelConfigMonedaSepMil" value="${DEFAULT_MONEDA.separadorMiles}" maxlength="1">
              </div>
            </div>
          </div>`;
    },

    _panelFichas() {
        return `
          <div class="tab-panel" id="panel-fichas">
            <div class="form-row">
              <div class="form-group">
                <label class="form-label">${t('Denominacion Base (centavos)')}</label>
                <input type="number" class="form-input" id="lotowheelConfigDenominacion" value="${DEFAULT_FICHAS.denominacion}" data-action="lotowheel:validatePremioMaximo">
                <div class="form-hint">${t('100 = 1 unidad de moneda')}</div>
              </div>
              <div class="form-group">
                <label class="form-label">${t('Premio Maximo (centavos)')}</label>
                <input type="number" class="form-input" id="lotowheelConfigPremioMax" value="${DEFAULT_LIMITES.premioMaximo}" data-action="lotowheel:validatePremioMaximo">
                <div class="form-hint">${t('Apuesta max pleno = Premio / 36')}</div>
              </div>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label class="form-label">${t('Vigencia de tickets (dias)')}</label>
                <input type="number" class="form-input" id="lotowheelConfigTicketExpiryDays" value="180" min="1" max="365">
                <div class="form-hint">${t('Dias que tiene el portador para cobrar un ticket ganador')}</div>
              </div>
            </div>
            <!-- Series de tickets — overrides por sorteo (superadmin only) -->
            <div id="lotowheelSeriesTicketsSection" style="display:none;">
              <hr style="margin: 16px 0; border-color: var(--border-color);">
              <label class="form-label" style="margin-bottom:8px;">${t('Series de tickets (override por sorteo)')}</label>
              <div class="form-hint" style="margin-bottom:10px;">${t('Dejá en "Heredar" para usar la serie del operador o la default global')}</div>
              <div class="form-row">
                <div class="form-group">
                  <label class="form-label">Bearer</label>
                  <select class="form-input" id="lotowheelConfigSerieBearer"><option value="">${t('Heredar')}</option></select>
                </div>
                <div class="form-group">
                  <label class="form-label">${t('Cuenta')}</label>
                  <select class="form-input" id="lotowheelConfigSerieCuenta"><option value="">${t('Heredar')}</option></select>
                </div>
                <div class="form-group">
                  <label class="form-label">VLT</label>
                  <select class="form-input" id="lotowheelConfigSerieVlt"><option value="">${t('Heredar')}</option></select>
                </div>
              </div>
              <!-- Overrides por partner_code (integradores externos) -->
              <div style="margin-top:14px;">
                <label class="form-label" style="margin-bottom:6px;">${t('Partners externos')}</label>
                <div class="form-hint" style="margin-bottom:8px;">${t('Opcional: asigná una serie específica a un integrador externo (identificado por su partner_code). Formato: letras, números, guiones o underscores — sin espacios.')}</div>
                <div id="lotowheelConfigSeriePartnersList"></div>
                <button type="button" class="btn btn-secondary btn-sm" data-action="lotowheel:agregarPartnerOverrideSerie" style="margin-top:6px;">
                  + ${t('Agregar partner')}
                </button>
              </div>
            </div>
            <div class="form-hint" id="hintPremioMax"></div>
            <hr style="margin: 20px 0; border-color: var(--border-color);">
            <div class="form-group">
              <label class="form-label">${t('Fichas Disponibles')}</label>
              <div class="chips-list" id="chipsList"></div>
              <button class="btn btn-secondary" style="margin-top:12px" data-action="lotowheel:addChip">
                ${icon('plus')} ${t('Agregar Ficha')}
              </button>
            </div>
          </div>`;
    },

    _panelSpin() {
        return `
          <div class="tab-panel" id="panel-spin">
            <div class="form-row">
              <div class="form-group">
                <label class="form-label">${t('Duracion Total (ms)')}</label>
                <input type="number" class="form-input" id="lotowheelConfigSpinDuration" value="${DEFAULT_SPIN.duration}">
              </div>
              <div class="form-group">
                <label class="form-label">${t('Correction Delay (ms)')}</label>
                <input type="number" class="form-input" id="lotowheelConfigSpinCorrection" value="${DEFAULT_SPIN.correctionDelay}">
              </div>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label class="form-label">Cruise RPM</label>
                <input type="number" class="form-input" id="lotowheelConfigSpinRPM" value="${DEFAULT_SPIN.cruiseRPM}">
              </div>
              <div class="form-group">
                <label class="form-label">${t('Tension Final')}</label>
                <input type="number" class="form-input" id="lotowheelConfigSpinTension" value="${DEFAULT_SPIN.tensionFinal}">
              </div>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label class="form-label">${t('FM Quesitos')}</label>
                <input type="number" class="form-input" id="lotowheelConfigSpinFM" value="${DEFAULT_SPIN.fmQuesitos}">
              </div>
            </div>
            <h4 style="margin:16px 0 8px; font-size:13px; color:var(--text-secondary)">${t('Fases (deben sumar 1.0)')}</h4>
            <div class="form-row">
              <div class="form-group">
                <label class="form-label">${t('Aceleracion')}</label>
                <input type="number" class="form-input" id="lotowheelConfigPhaseAccel" value="${DEFAULT_SPIN.phases.accel}" step="0.01">
              </div>
              <div class="form-group">
                <label class="form-label">${t('Crucero')}</label>
                <input type="number" class="form-input" id="lotowheelConfigPhaseCruise" value="${DEFAULT_SPIN.phases.cruise}" step="0.01">
              </div>
              <div class="form-group">
                <label class="form-label">${t('Desaceleracion')}</label>
                <input type="number" class="form-input" id="lotowheelConfigPhaseDecel" value="${DEFAULT_SPIN.phases.decel}" step="0.01">
              </div>
            </div>
          </div>`;
    },

    _panelApuestas() {
        return `
          <div class="tab-panel" id="panel-apuestas">
            <!-- Header explicativo de las dos columnas T/W (§8.14) -->
            <div class="bet-canal-leyenda">
              <strong>T</strong> = Ticket al portador (POS / cashier / TV)
              &nbsp;·&nbsp;
              <strong>W</strong> = Wallet de jugador identificado (front-customer)
            </div>
            <!-- Seccion: Seleccion Directa -->
            <div class="bet-section">
              <h4 class="bet-section-title">${t('Seleccion Directa')}</h4>
              <p class="bet-section-desc">${t('Premios sobre numeros especificos en el panel de apuesta')}</p>
              <div class="bet-toggles">
                ${this._betToggle(t('Duo'), 'multDuo', 'x18', t('2 numeros adyacentes'), 'betDuo')}
                ${this._betToggle('LOTO Duo', 'multLotoDuo', 'x18', t('LOTO con 1, 2 o 3'), 'betLotoDuo', 'betLotoDuoDesc')}
                ${this._betToggle(t('Trio'), 'multTrio', 'x12', t('Fila de 3 numeros'), 'betTrio', null, 'betTrioRow')}
                ${this._betToggle(t('Cuarteto'), 'multCuarteto', 'x9', t('Esquina de 4 numeros'), 'betCuarteto', null, 'betCuartetoRow')}
                ${this._betToggle(t('Sexteto'), 'multSexteto', 'x6', t('2 filas (6 numeros)'), 'betSexteto', null, 'betSextetoRow')}
              </div>
            </div>

            <!-- Seccion: Cualitativa -->
            <div class="bet-section">
              <h4 class="bet-section-title">${t('Cualitativa')}</h4>
              <p class="bet-section-desc">${t('Premios por propiedades del numero')}</p>
              <div class="bet-toggles">
                ${this._betToggle(t('Rojo / Negro'), null, 'x2', t('Color del numero'), 'betColor')}
                ${this._betToggle(t('Par / Impar'), null, 'x2', t('Paridad del numero'), 'betParidad', null, 'betParidadRow')}
                ${this._betToggle(t('Mitad (1-18 / 19-36)'), null, 'x2', t('Primera o segunda mitad'), 'betMitad', null, 'betMitadRow', 'betMitadName')}
              </div>
            </div>

            <!-- Seccion: Grupos -->
            <div class="bet-section">
              <h4 class="bet-section-title">${t('Grupos')}</h4>
              <p class="bet-section-desc">${t('Conjuntos predefinidos de numeros')}</p>
              <div class="bet-toggles">
                ${this._betToggle(t('Tercios'), null, 'x3', '1-12, 13-24, 25-36', 'betTercios', 'betTerciosDesc', 'betTerciosRow')}
                ${this._betToggle(t('Columnas'), null, 'x3', t('Columnas verticales del panel de apuesta'), 'betColumnas', null, 'betColumnasRow')}
                ${this._betToggle(t('Bloques (A-F)'), 'betBloquesMult', 'x6', t('Sectores fisicos de la rueda'), 'betBloques', null, 'betBloquesRow', 'betBloquesName')}
                ${this._betToggle(t('Espejos'), null, 'x18', t('Numeros invertidos (12-21, 13-31, 23-32)'), 'betEspejos', null, 'betEspejosRow')}
                ${this._betToggle(t('Gemelos'), null, 'x12', '11, 22, 33', 'betGemelos', null, 'betGemelosRow')}
              </div>
            </div>
          </div>`;
    },

    _panelResolver() {
        return `
          <div class="tab-panel" id="panel-resolver">

            <!-- Modo + Preset + Simular: fila de labels, luego fila de controles -->
            <div style="display:flex; gap:12px; align-items:center; margin-bottom:4px;">
              <label class="form-label" style="margin:0; flex:1;">${t('Modo')}</label>
              <label class="form-label" style="margin:0; flex:2;">${t('Preset')}</label>
              <button class="btn btn-sm btn-secondary" data-action="lotowheel:saveCurrentAsPreset" style="font-size:11px; padding:3px 10px; flex:0 0 auto;">+ ${t('Guardar actual')}</button>
              <div style="flex:0 0 auto; visibility:hidden; pointer-events:none;" aria-hidden="true">
                <button class="btn btn-primary">Simular</button>
              </div>
            </div>
            <div style="display:flex; gap:12px; align-items:center;">
              <div style="flex:1; min-width:0;">
                <select class="form-select" id="resolverName">
                  <option value="">${t('Cargando...')}</option>
                </select>
              </div>
              <div style="flex:2; min-width:0;">
                <div id="presetSearchWrapper" style="position:relative;">
                  <input type="text" id="presetSearchInput" class="form-input"
                    placeholder="${t('Buscar o seleccionar preset...')}"
                    autocomplete="off"
                    data-action="lotowheel:onPresetSearch"
                    style="width:100%; padding-right:32px;">
                  <span id="presetSearchClear" data-action="lotowheel:clearPresetSearch"
                    style="display:none; position:absolute; right:10px; top:50%; transform:translateY(-50%);
                           cursor:pointer; color:var(--text-muted); font-size:16px; line-height:1;">×</span>
                </div>
                <div id="presetDropdown" style="display:none; border:1px solid var(--border-color);
                     border-radius:6px; overflow:hidden; max-height:220px; overflow-y:auto;
                     background:var(--bg-secondary); margin-top:2px;"></div>
              </div>
              <div style="flex:0 0 auto;">
                <button class="btn btn-primary" data-action="lotowheel:simulateProfile" id="btnSimulate">${t('Simular')}</button>
              </div>
            </div>
            <div id="presetActiveLabel" style="font-size:11px; color:var(--text-muted); margin-top:3px;"></div>

            <!-- Perfil de Operador (solo Loteria Ponderada) -->
            <div class="profile-section" id="profileSection" style="display:none;">
              <hr style="border-color:var(--border-color); margin:10px 0 8px;">
              <div id="profileSlidersContainer"></div>
            </div>

            <!-- RTP por canal (ticket/wallet) — SIEMPRE visible para volatilidad-controlada.
                 Los inputs se montan acá desde el configSchema (whitelist en lotowheel-modal-algoritmo.js).
                 El resto de params tecnicos queda en el <details> oculto de abajo. -->
            <div id="resolverConfigVisibleRTP" style="margin-top:12px; display:none;"></div>

            <!-- Config tecnica — Para activar: cambiar MOSTRAR_PARAMS_AVANZADOS a true en lotowheel.js -->
            <details id="resolverConfigDetails" style="margin-top:12px; display:none;">
              <summary style="cursor:pointer; color:var(--text-secondary); font-size:13px;">${t('Parametros Avanzados')}</summary>
              <div id="resolverConfigFields" style="margin-top:8px;"></div>
            </details>

            <!-- Lista completa de presets (oculta, solo para referencia legacy / acciones rápidas) -->
            <div id="presetsList" style="display:none;"></div>
          </div>`;
    },

    _betToggle(name, multId, mult, desc, checkId, descId, rowId, nameId) {
        // §8.14 — dos columnas independientes por canal de venta (Ticket /
        // Wallet). El `checkId` original se preserva en el contenedor
        // (testing/grep) y se derivan `<checkId>_ticket` y `<checkId>_wallet`
        // para los dos checkboxes reales. La capa JS lee siempre por canal:
        // el id histórico ya no es un input, solo un anchor del DOM.
        const rowIdAttr = rowId ? ` id="${rowId}"` : '';
        const nameIdAttr = nameId ? ` id="${nameId}"` : '';
        const descIdAttr = descId ? ` id="${descId}"` : '';
        const multIdAttr = multId ? ` id="${multId}"` : '';
        return `
                <div class="bet-toggle-row bet-toggle-row--dual-canal"${rowIdAttr} data-bet-group-id="${checkId}">
                  <div class="bet-toggle-info">
                    <span class="bet-toggle-name"${nameIdAttr}>${name}</span>
                    <span class="bet-toggle-mult"${multIdAttr}>${mult}</span>
                  </div>
                  <span class="bet-toggle-desc"${descIdAttr}>${desc}</span>
                  <div class="bet-toggle-canales">
                    <label class="toggle-switch toggle-switch--canal" title="${t('Ticket (POS / cashier / TV)')}">
                      <span class="toggle-canal-label">T</span>
                      <input type="checkbox" id="${checkId}_ticket" checked>
                      <span class="toggle-slider"></span>
                    </label>
                    <label class="toggle-switch toggle-switch--canal" title="${t('Wallet (jugador identificado)')}">
                      <span class="toggle-canal-label">W</span>
                      <input type="checkbox" id="${checkId}_wallet" checked>
                      <span class="toggle-slider"></span>
                    </label>
                  </div>
                </div>`;
    },

    // ═══════════════════════════════════════════════════════════════════════
    // SIMULATOR — modal del simulador (controles + canvas Chart.js).
    // Cableado al objeto LotowheelSimulator (lotowheel-simulator.js).
    // ═══════════════════════════════════════════════════════════════════════
    simulator() {
        return `
    <div class="modal" id="modalSimulator" style="display:none; max-width:1100px;">
      <div class="modal-header">
        <h2>${t('Simulador de Ecosistema')}</h2>
        <button class="btn-icon" data-action="simulator:close">
          ${icon('x')}
        </button>
      </div>
      <div class="modal-body" style="flex-direction:column; padding:20px;">

        <!-- Selector de resolver principal + comparación -->
        <div style="display:flex; gap:12px; align-items:center; margin-bottom:16px; flex-wrap:wrap;">
          <div style="flex:1; min-width:200px;">
            <label class="form-label" style="font-size:11px; margin-bottom:4px;">${t('Simular')}</label>
            <select class="form-select" id="simResolverSelect" data-action="simulator:onResolverChange">
              <!-- Opciones: resolvers + presets, se llenan dinámicamente -->
            </select>
          </div>
          <div style="flex:1; min-width:200px;">
            <label class="form-label" style="font-size:11px; margin-bottom:4px;">${t('Comparar con')}</label>
            <select class="form-select" id="simCompareWith">
              <option value="">${t('Sin comparación')}</option>
            </select>
          </div>
          <div style="align-self:flex-end;">
            <button class="btn btn-primary" id="btnRunSimulation"
                    data-action="simulator:runSimulation" style="white-space:nowrap; min-width:180px;">
              ${t('Ejecutar Simulación')}
            </button>
          </div>
        </div>

        <!-- Parametros de simulacion -->
        <div style="background:var(--bg-glass); padding:16px; border-radius:8px; border:1px solid var(--border-glass);">
          <!-- Fila 1: Volumen por sorteo -->
          <div class="form-row" style="gap:20px;">
            <div class="form-group" style="flex:1">
              <label class="form-label">${t('Escenario')}</label>
              <select class="form-select" id="simParamEscenario" data-action="simulator:onEscenarioChange">
                <option value="estable" selected>${t('Operación normal')}</option>
                <option value="local_nuevo">${t('Local nuevo')}</option>
                <option value="concentrada">${t('Apuesta concentrada')}</option>
                <option value="recuperacion">${t('Recuperación')}</option>
              </select>
              <span id="simParamEscenarioDesc" style="font-size:11px;color:var(--text-secondary);margin-top:2px;display:block;">Flujo constante de jugadores, sin eventos especiales</span>
            </div>
            <div class="form-group" style="flex:1" id="simParamSorteosGroup">
              <label class="form-label">${t('Cantidad de sorteos')}</label>
              <select class="form-select" id="simParamSorteos">
                <option value="20">20 (~1 hora)</option>
                <option value="50">50 (~3 horas)</option>
                <option value="110" selected>110 (~1 día)</option>
                <option value="330">330 (~3 días)</option>
                <option value="770">770 (~1 semana)</option>
                <option value="1500">1,500 (~2 semanas)</option>
                <option value="3300">3,300 (~1 mes)</option>
              </select>
            </div>
            <div class="form-group" style="flex:1; display:none;" id="simParamDeficitGroup">
              <label class="form-label">${t('Déficit inicial')}</label>
              <div style="display:flex; align-items:center; gap:6px;">
                <span style="color:var(--text-secondary);">$</span>
                <input type="number" class="form-input" id="simParamDeficit" min="100" max="1000000" value="5000"
                       style="flex:1;" placeholder="5000">
              </div>
              <span style="font-size:11px;color:var(--text-secondary);margin-top:2px;display:block;">${t('Simula hasta recuperar el monto')}</span>
            </div>
            <div class="form-group" style="flex:1">
              <label class="form-label">${t('Boletos por sorteo')}</label>
              <div style="display:flex; align-items:center; gap:8px;">
                <input type="range" id="simParamJugadasPorSorteo" min="1" max="200" value="15" style="flex:1"
                       data-action="simulator:onSimParamChange">
                <span id="simParamJugadasPorSorteoVal" style="min-width:20px; text-align:center; color:var(--text-primary);">15</span>
              </div>
            </div>
            <div class="form-group" style="flex:1">
              <label class="form-label">${t('Apuestas promedio por boleto')}</label>
              <div style="display:flex; align-items:center; gap:8px;">
                <input type="range" id="simParamApuestasPorJugada" min="1" max="15" value="3" style="flex:1"
                       data-action="simulator:onSimParamChange">
                <span id="simParamApuestasPorJugadaVal" style="min-width:20px; text-align:center; color:var(--text-primary);">3</span>
              </div>
            </div>
            <div class="form-group" style="flex:1">
              <label class="form-label">${t('Monto max. por apuesta')}</label>
              <div style="display:flex; align-items:center; gap:8px;">
                <input type="range" id="simParamMontoApuesta" min="1" max="100" value="10" style="flex:1"
                       data-action="simulator:onSimParamChange">
                <span id="simParamMontoApuestaVal" style="min-width:20px; text-align:center; color:var(--text-primary);">10</span>
              </div>
            </div>
          </div>
          <!-- Fila 2: Ecosistema y comportamiento -->
          <div class="form-row" style="gap:20px; margin-top:12px;">
            <div class="form-group" style="flex:1">
              <label class="form-label">${t('Puntos de venta')}</label>
              <div style="display:flex; align-items:center; gap:8px;">
                <input type="range" id="simParamLocales" min="1" max="20" value="3" style="flex:1"
                       data-action="simulator:onSimParamChange">
                <span id="simParamLocalesVal" style="min-width:20px; text-align:center; color:var(--text-primary);">3</span>
              </div>
            </div>
            <div class="form-group" style="flex:1">
              <label class="form-label">${t('Jugadores por punto de venta')}</label>
              <div style="display:flex; align-items:center; gap:8px;">
                <input type="range" id="simParamJugadores" min="1" max="200" value="15" style="flex:1"
                       data-action="simulator:onSimParamChange">
                <span id="simParamJugadoresVal" style="min-width:20px; text-align:center; color:var(--text-primary);">15</span>
              </div>
            </div>
            <div class="form-group" style="flex:1">
              <label class="form-label">${t('% venta en ticket impreso')}</label>
              <div style="display:flex; align-items:center; gap:8px;">
                <input type="range" id="simParamTickets" min="0" max="100" value="50" style="flex:1"
                       data-action="simulator:onSimParamChange">
                <span id="simParamTicketsVal" style="min-width:30px; text-align:center; color:var(--text-primary);">50%</span>
              </div>
            </div>
            <div class="form-group" style="flex:1">
              <label class="form-label">${t('Jugador retira si gana mas de')}</label>
              <div style="display:flex; align-items:center; gap:8px;">
                <input type="range" id="simParamRetiroGanancia" min="0" max="10" value="3" style="flex:1"
                       data-action="simulator:onSimParamChange">
                <span id="simParamRetiroGananciaVal" style="min-width:30px; text-align:center; color:var(--text-primary);">3x</span>
              </div>
            </div>
          </div>

          <!-- Espacio visual inferior -->
          <div style="height:4px;"></div>
        </div>

        <!-- Resultados -->
        <div id="simResultsSection" style="display:none; margin-top:20px;">
          <!-- KPIs arriba de todo -->
          <div id="simKpis"></div>
          <!-- Gráficas se generan dinámicamente -->
          <div id="simChartsContainer" style="margin-top:16px;"></div>
        </div>

      </div>
      <div class="modal-footer" style="justify-content:space-between;">
        <button class="btn btn-secondary" data-action="simulator:close">${t('Volver')}</button>
        <button class="btn btn-primary" data-action="simulator:saveAndClose">${t('Guardar')}</button>
      </div>
    </div>`;
    },

    _panelPozosComunes() {
        return `
          <div class="tab-panel" id="panel-pozos-comunes">
            <div style="margin-bottom:20px;">
              <p style="font-size:14px; color:var(--text-muted); line-height:1.5; margin:0;">
                ${t('Selecciona los jackpots a los que contribuirá este sorteo y configura el porcentaje de cada apuesta que se aporta al bote.')}
              </p>
            </div>
            <div id="pozosComunesListSorteo" style="margin-bottom:16px;">
              <div class="text-muted" style="font-size:14px; padding:16px 0;">${t('Cargando...')}</div>
            </div>
          </div>`;
    },

    _panelAcceso() {
        return `
          <div class="tab-panel" id="panel-acceso">
            <div id="accesoSoloSuperadmin" style="display:none;">
              <!-- Mensaje dinámico según caso: no-superadmin, o sorteo de operador -->
              <div id="accesoSoloSuperadminMsg" style="color:var(--text-muted); font-size:13px; padding:12px 0;"></div>
            </div>
            <div id="accesoPanel">
              <!-- Badge de estado: se actualiza dinámicamente en _renderAccesoEstadoBadge() -->
              <div id="accesoEstadoBadge" style="margin-bottom:14px;"></div>
              <div style="display:flex; gap:8px; margin-bottom:12px;">
                <input type="text" id="accesoNodoSearch" class="form-input" placeholder="${t('Buscar operador por nombre...')}"
                  data-action="lotowheel:onAccesoSearch" style="flex:1;">
                <button class="btn btn-sm btn-secondary" data-action="lotowheel:clearAccesoNodoSearch">${t('Limpiar')}</button>
              </div>
              <div id="accesoNodoList" style="max-height:260px; overflow-y:auto; border:1px solid var(--border-color); border-radius:8px; padding:8px;">
                <div class="text-muted" style="font-size:13px;">${t('Cargando...')}</div>
              </div>
              <div style="margin-top:10px; display:flex; gap:8px; justify-content:flex-end;">
                <button class="btn btn-sm btn-secondary" data-action="lotowheel:selectAllAccesoNodos">${t('Marcar todos')}</button>
                <button class="btn btn-sm btn-secondary" data-action="lotowheel:clearAllAccesoNodos">${t('Desmarcar todos')}</button>
              </div>
            </div>
          </div>`;
    },
};
